import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  Alignment,
  Autoformat,
  AutoImage,
  AutoLink,
  Autosave,
  BalloonToolbar,
  BlockQuote,
  Bold,
  Bookmark,
  ClassicEditor,
  Code,
  CodeBlock,
  Emoji,
  Essentials,
  FindAndReplace,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  GeneralHtmlSupport,
  Heading,
  Highlight,
  HorizontalLine,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  MediaEmbed,
  Mention,
  Paragraph,
  PasteFromOffice,
  PlainTableOutput,
  Plugin,
  RemoveFormat,
  SimpleUploadAdapter,
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  SpecialCharactersText,
  Strikethrough,
  Style,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableLayout,
  TableProperties,
  TableToolbar,
  TextTransformation,
  Underline,
} from "ckeditor5";
import { useEffect, useMemo, useRef, useState } from "react";

import "ckeditor5/ckeditor5.css";
import "./style.scss";

import { Spin } from "antd";
import apiFactory from "../../api";
import { RESOURCE } from "../../api/apiConstant";
import { useMenuContext } from "../../context/MenuContext";
import { formatSecondTime } from "../../utils/formatTime";
import { blobToBase64, extractContent, isBase64 } from "../../utils/Utils";
import SelectivePaste from "./customPlugin/SelectivePaste";
import { UploadAdapterPlugin } from "./customPlugin/UploadAdapter";

const LICENSE_KEY = "GPL";

class AllowDataResourceId extends Plugin {
  static get pluginName() {
    return "AllowDataResourceId";
  }

  init() {
    const editor = this.editor;

    editor.model.schema.extend("imageBlock", {
      allowAttributes: ["data-resource-id"],
    });
    editor.model.schema.extend("imageInline", {
      allowAttributes: ["data-resource-id"],
    });

    editor.conversion.for("downcast").attributeToAttribute({
      model: "data-resource-id",
      view: "data-resource-id",
    });

    editor.conversion.for("upcast").attributeToAttribute({
      view: "data-resource-id",
      model: "data-resource-id",
    });
  }
}

const EmailComposer = ({
  originalContent,
  notification,
  setMailContent,
  mailContent,
  conversationId,
  isForwardMail,
  sender,
}) => {
  const { setKeydownMenuOn } = useMenuContext();
  const editorContainerRef = useRef(null);
  const editorRef = useRef(null);

  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const imageIdsRef = useRef([]);

  const extractImageIds = (htmlString) => {
    if (!htmlString) return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    const elements = doc.querySelectorAll("[data-resource-id]");

    const ids = Array.from(elements)
      .map((el) => {
        return el.dataset.resourceId;
      })
      .filter(Boolean);

    return [...new Set(ids)];
  };

  const handleChange = async (event, editor) => {
    if (editor) {
      const newContent = editor.getData();

      setMailContent((prev) => ({ ...prev, content: newContent }));

      const currentIds = extractImageIds(newContent);
      const prevIds = imageIdsRef.current;

      const deletedIds = prevIds.filter((id) => !currentIds.includes(id));

      if (deletedIds.length > 0 && conversationId) {
        await apiFactory.resourceApi
          .deleteFiles({
            resourceIds: deletedIds,
            conversationId: conversationId,
          })
          .catch((err) => console.error("Failed to delete files", err));
      }

      imageIdsRef.current = currentIds;

      setMailContent((prev) => ({
        ...prev,
        content: newContent,
      }));
    }
  };

  const convertImgToEditor = async (contentInput) => {
    setIsLoadingContent(true);

    try {
      const { doc, images } = extractContent(contentInput);

      if (images?.length > 0) {
        const imagePromises = images.map(async (imgDoc) => {
          if (!imgDoc?.src) return;

          if (isBase64(imgDoc.src)) return;

          if (!imgDoc.src.includes(RESOURCE)) return;

          const parts = imgDoc.src.split("/");
          const resourceId = parts.pop();

          if (!resourceId || resourceId === "null") return;

          try {
            const fetchedBlob =
              await apiFactory.resourceApi.getStreamFileAsBlob(resourceId);

            if (
              !fetchedBlob ||
              fetchedBlob.size === 0 ||
              fetchedBlob.type.startsWith("application/json")
            ) {
              imgDoc.remove();
              return;
            }

            const base64Data = await blobToBase64(fetchedBlob);

            imgDoc.src = base64Data;
            imgDoc.dataset.resourceId = resourceId;
          } catch (error) {
            console.error("Failed to load image:", resourceId, error);
            imgDoc.remove();
          }
        });

        await Promise.all(imagePromises);
      }

      const cleanedHTML = doc.body.innerHTML;

      setMailContent({
        oldContent: cleanedHTML,
        content: isForwardMail
          ? `
        <p>&nbsp;</p>
        <p>--------- ${sender?.name}님이 보낸 메시지 (${formatSecondTime(notification?.createdAt)}) ---------</p>
        ${cleanedHTML}
        `
          : cleanedHTML,
      });
    } finally {
      setIsLoadingContent(false);
    }
  };

  const { editorConfig } = useMemo(() => {
    if (!isLayoutReady) {
      return {};
    }

    return {
      editorConfig: {
        toolbar: {
          items: [
            "findAndReplace",
            "|",
            "heading",
            "style",
            "|",
            "fontSize",
            "fontFamily",
            "fontColor",
            "fontBackgroundColor",
            "|",
            "bold",
            "italic",
            "underline",
            "strikethrough",
            "subscript",
            "superscript",
            "code",
            "codeBlock",
            "removeFormat",
            "|",
            "emoji",
            "specialCharacters",
            "horizontalLine",
            "link",
            "bookmark",
            "insertImage",
            "insertImageViaUrl",
            "mediaEmbed",
            "insertTable",
            "highlight",
            "blockQuote",
            "|",
            "alignment",
            "|",
            "outdent",
            "indent",
          ],
          shouldNotGroupWhenFull: false,
        },
        plugins: [
          Alignment,
          Autoformat,
          AutoImage,
          AutoLink,
          Autosave,
          BalloonToolbar,
          SimpleUploadAdapter,
          BlockQuote,
          Bold,
          Bookmark,
          Code,
          CodeBlock,
          Emoji,
          Essentials,
          FindAndReplace,
          FontBackgroundColor,
          FontColor,
          FontFamily,
          FontSize,
          GeneralHtmlSupport,
          Heading,
          Highlight,
          HorizontalLine,
          ImageBlock,
          ImageCaption,
          ImageInline,
          ImageInsert,
          ImageInsertViaUrl,
          ImageResize,
          ImageStyle,
          ImageTextAlternative,
          ImageToolbar,
          ImageUpload,
          Indent,
          IndentBlock,
          Italic,
          Link,
          LinkImage,
          MediaEmbed,
          Mention,
          Paragraph,
          PasteFromOffice,
          PlainTableOutput,
          RemoveFormat,
          SpecialCharacters,
          SpecialCharactersArrows,
          SpecialCharactersCurrency,
          SpecialCharactersEssentials,
          SpecialCharactersLatin,
          SpecialCharactersMathematical,
          SpecialCharactersText,
          Strikethrough,
          Style,
          Subscript,
          Superscript,
          Table,
          TableCaption,
          TableCellProperties,
          TableColumnResize,
          TableLayout,
          TableProperties,
          TableToolbar,
          TextTransformation,
          Underline,
          AllowDataResourceId,
          SelectivePaste,
        ],
        htmlSupport: {
          allow: [
            {
              name: /.*/,
              attributes: true,
              classes: true,
              styles: true,
            },

            {
              name: "div",
              attributes: true,
              classes: true,
              styles: true,
            },
            {
              name: "p",
              attributes: true,
              classes: true,
              styles: true,
            },
            {
              name: "br",
            },
            {
              name: "span",
              attributes: true,
              classes: true,
              styles: true,
            },
          ],
          disallow: [],
        },
        image: {
          toolbar: [
            "toggleImageCaption",
            "imageTextAlternative",
            "|",
            "imageStyle:inline",
            "imageStyle:wrapText",
            "imageStyle:breakText",
            "|",
            "resizeImage",
          ],
        },
        licenseKey: LICENSE_KEY,
      },
    };
  }, [isLayoutReady]);

  useEffect(() => {
    setIsLayoutReady(true);
    return () => setIsLayoutReady(false);
  }, []);

  useEffect(() => {
    convertImgToEditor(originalContent);
  }, [conversationId, originalContent]);

  useEffect(() => {
    const initialIds = extractImageIds(mailContent?.content);
    imageIdsRef.current = initialIds;
  }, []);

  if (isLoadingContent) {
    return (
      <div className="w-full flex justify-center items-center h-[450px]">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="main-container">
      <button
        className="w-[100%] cursor-text"
        ref={editorContainerRef}
        onFocus={() => setKeydownMenuOn(false)}
        onBlur={() => setKeydownMenuOn(true)}
      >
        <div className="editor-container__editor custom-edit-content">
          <div ref={editorRef}>
            {editorConfig && (
              <CKEditor
                editor={ClassicEditor}
                config={editorConfig}
                data={mailContent?.content || ""}
                onChange={handleChange}
                onReady={(editor) => {
                  UploadAdapterPlugin(editor, conversationId || null);
                }}
              />
            )}
          </div>
        </div>
      </button>
    </div>
  );
};

export default EmailComposer;
